import api from './api';
import {
  ANALYTICS_STUDENT_PERFORMANCE,
  ANALYTICS_TOPIC_PERFORMANCE,
  ANALYTICS_CONFIDENCE_ACCURACY,
  ANALYTICS_TEACHER_DASHBOARD,
  ANALYTICS_STUDENT_DASHBOARD
} from '../config/Config';

/**
 * Analytics Service for Test Performance Analysis
 * Provides comprehensive analytics for students, teachers, and topics
 */

const analyticsService = {
  /**
   * Get comprehensive student performance analytics
   * @param {number} studentId - Optional student ID (if not provided, uses authenticated user)
   * @param {number} days - Number of days to analyze (default: 90)
   * @returns {Promise} Analytics data including performance timeline, subject breakdown, etc.
   */
  getStudentPerformance: async (studentId = null, days = 90) => {
    try {
      const params = { days };
      const url = studentId 
        ? `${ANALYTICS_STUDENT_PERFORMANCE}${studentId}/`
        : ANALYTICS_STUDENT_PERFORMANCE;
      
      const response = await api.get(url, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching student performance analytics:', error);
      throw error;
    }
  },

  /**
   * Get detailed topic performance analytics
   * @param {string} topicName - Name of the topic
   * @param {string} subjectName - Name of the subject
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Topic-specific analytics data
   */
  getTopicPerformance: async (topicName, subjectName, studentId = null, days = 90) => {
    try {
      const params = { topic: topicName, subject: subjectName, days };
      if (studentId) {
        params.student_id = studentId;
      }
      
      const response = await api.get(ANALYTICS_TOPIC_PERFORMANCE, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching topic performance analytics:', error);
      throw error;
    }
  },

  /**
   * Get topic performance by topic ID
   * @param {number} topicId - ID of the topic
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Topic-specific analytics data
   */
  getTopicPerformanceById: async (topicId, studentId = null, days = 90) => {
    try {
      const params = { days };
      if (studentId) {
        params.student_id = studentId;
      }
      
      const response = await api.get(`${ANALYTICS_TOPIC_PERFORMANCE}${topicId}/`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching topic performance analytics by ID:', error);
      throw error;
    }
  },

  /**
   * Get confidence vs accuracy correlation analysis
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Confidence accuracy correlation data
   */
  getConfidenceAccuracyCorrelation: async (studentId = null, days = 90) => {
    try {
      const params = { days };
      if (studentId) {
        params.student_id = studentId;
      }
      
      const response = await api.get(ANALYTICS_CONFIDENCE_ACCURACY, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching confidence accuracy correlation:', error);
      throw error;
    }
  },

  /**
   * Get teacher dashboard analytics for all students
   * @param {number} days - Number of days to analyze
   * @param {string} teacherId - Optional teacher ID for demo mode
   * @returns {Promise} Teacher dashboard analytics data
   */
  getTeacherDashboard: async (days = 30, demoMode = false, teacherId = null) => {
    try {
      const params = { days };
      if (teacherId) {
        params.teacher_id = teacherId;
      }
      if (demoMode) {
        params.demo_mode = true;
      }
      
      console.log('🚀 Analytics Service - Request params:', params);
      console.log('🚀 Analytics Service - Demo mode:', demoMode);
      console.log('🚀 Analytics Service - Endpoint:', ANALYTICS_TEACHER_DASHBOARD);
      
      const response = await api.get(ANALYTICS_TEACHER_DASHBOARD, { params });
      
      console.log('🚀 Analytics Service - Response:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching teacher dashboard analytics:', error);
      throw error;
    }
  },

  /**
   * Get student performance comparison across multiple students
   * @param {Array} studentIds - Array of student IDs to compare
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Comparison data for multiple students
   */
  getStudentComparison: async (studentIds, days = 90) => {
    try {
      const comparisonData = [];
      
      for (const studentId of studentIds) {
        const studentData = await analyticsService.getStudentPerformance(studentId, days);
        comparisonData.push(studentData);
      }
      
      return comparisonData;
    } catch (error) {
      console.error('Error fetching student comparison data:', error);
      throw error;
    }
  },

  /**
   * Get performance trends over time
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Performance trends data
   */
  getPerformanceTrends: async (studentId = null, days = 90) => {
    try {
      const performanceData = await analyticsService.getStudentPerformance(studentId, days);
      return performanceData.performance_timeline;
    } catch (error) {
      console.error('Error fetching performance trends:', error);
      throw error;
    }
  },

  /**
   * Get subject performance breakdown
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Subject performance data
   */
  getSubjectPerformance: async (studentId = null, days = 90) => {
    try {
      const performanceData = await analyticsService.getStudentPerformance(studentId, days);
      return performanceData.subject_performance;
    } catch (error) {
      console.error('Error fetching subject performance:', error);
      throw error;
    }
  },

  /**
   * Get topic performance breakdown from student performance
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Topic performance data
   */
  getTopicPerformanceBreakdown: async (studentId = null, days = 90) => {
    try {
      const performanceData = await analyticsService.getStudentPerformance(studentId, days);
      return performanceData.topic_performance;
    } catch (error) {
      console.error('Error fetching topic performance:', error);
      throw error;
    }
  },

  /**
   * Get confidence analysis data
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Confidence analysis data
   */
  getConfidenceAnalysis: async (studentId = null, days = 90) => {
    try {
      const performanceData = await analyticsService.getStudentPerformance(studentId, days);
      return performanceData.confidence_analysis;
    } catch (error) {
      console.error('Error fetching confidence analysis:', error);
      throw error;
    }
  },

  /**
   * Get test type performance breakdown
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Test type performance data
   */
  getTestTypePerformance: async (studentId = null, days = 90) => {
    try {
      const performanceData = await analyticsService.getStudentPerformance(studentId, days);
      return performanceData.test_type_performance;
    } catch (error) {
      console.error('Error fetching test type performance:', error);
      throw error;
    }
  },

  /**
   * Get time analysis data
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Time analysis data
   */
  getTimeAnalysis: async (studentId = null, days = 90) => {
    try {
      const performanceData = await analyticsService.getStudentPerformance(studentId, days);
      return performanceData.time_analysis;
    } catch (error) {
      console.error('Error fetching time analysis:', error);
      throw error;
    }
  },

  /**
   * Get recent activity data
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Recent activity data
   */
  getRecentActivity: async (studentId = null, days = 90) => {
    try {
      const performanceData = await analyticsService.getStudentPerformance(studentId, days);
      return performanceData.recent_activity;
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  },

  /**
   * Export analytics data for a specific student
   * @param {number} studentId - Student ID
   * @param {number} days - Number of days to analyze
   * @param {string} format - Export format ('csv', 'json', 'pdf')
   * @returns {Promise} Export data or download link
   */
  exportStudentAnalytics: async (studentId, days = 90, format = 'json') => {
    try {
      const params = { days, format };
      const response = await api.get(`${ANALYTICS_STUDENT_PERFORMANCE}${studentId}/export/`, { params });
      return response.data;
    } catch (error) {
      console.error('Error exporting student analytics:', error);
      throw error;
    }
  },

  /**
   * Get analytics summary for quick dashboard display
   * @param {number} studentId - Optional student ID
   * @param {number} days - Number of days to analyze
   * @returns {Promise} Summary analytics data
   */
  getAnalyticsSummary: async (studentId = null, days = 90) => {
    try {
      const performanceData = await analyticsService.getStudentPerformance(studentId, days);
      
      return {
        summary: performanceData.summary,
        recentActivity: performanceData.recent_activity?.slice(0, 5) || [],
        topTopics: performanceData.topic_performance?.slice(0, 5) || [],
        confidenceOverview: performanceData.confidence_analysis?.overall_confidence_breakdown || []
      };
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      throw error;
    }
  },

  /**
   * Get student dashboard analytics (main dashboard view)
   * @param {number|string} studentId - Optional student ID or email
   * @param {Date} fromDate - Start date for analysis
   * @param {Date} toDate - End date for analysis
   * @param {string} selectedTopic - Optional topic filter
   * @param {string} selectedExamMode - Optional exam mode filter
   * @returns {Promise} Student dashboard analytics data
   */
  getStudentDashboard: async (
    studentId = null,
    fromDate = null,
    toDate = null,
    selectedTopic = null,
    selectedExamMode = null,
    demoMode = false
  ) => {
    try {
      const params = {};
      
      // Add date parameters if provided
      if (fromDate) {
        params.from_date = fromDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      }
      if (toDate) {
        params.to_date = toDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      }
      
      // Add student_id parameter if provided (for demo mode)
      if (studentId) {
        params.student_id = studentId;
      }

      if (demoMode) {
        params.demo_mode = 'true';
      }
      
      // Add topic filter if provided
      if (selectedTopic && selectedTopic !== 'all') {
        params.topic = selectedTopic;
      }
      
      // Add exam mode filter if provided
      if (selectedExamMode && selectedExamMode !== 'all') {
        params.exam_mode = selectedExamMode;
      }
      
      // Always use the base analytics endpoint - the backend will handle student identification
      // through the request context (authenticated user) or query parameters
      const response = await api.get(ANALYTICS_STUDENT_DASHBOARD, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching student dashboard analytics:', error);
      throw error;
    }
  }
};

export default analyticsService;
